import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { OrderTimeline } from "@/components/OrderTimeline";
import { Plus, Edit2, Trash2, Package, ShoppingCart, X, Tag, BarChart3, Bell, Upload, Image as ImageIcon, MapPin, Copy, ExternalLink, Phone as PhoneIcon, Gift, Award, Sparkles } from "lucide-react";

const CATS = ["cases", "headphones", "chargers", "power_banks", "small_accessories"];
const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

const empty = {
  name_en: "", name_ar: "", description_en: "", description_ar: "",
  price: 0, category: "cases", image_url: "", stock: 100, featured: false, points: 0,
};

export default function Admin() {
  const { user, isAdmin } = useAuth();
  const { t, lang } = useLanguage();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDelete, setConfirmDelete] = useState(null); // product object or null
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catUploading, setCatUploading] = useState(""); // which category key is uploading
  const loadCategories = () => api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});

  const [contactInfo, setContactInfo] = useState({ phone: "", whatsapp: "", email: "", address_ar: "", address_en: "" });
  const [contactSaving, setContactSaving] = useState(false);
  const loadContactInfo = () => api.get("/contact-info").then(({ data }) => setContactInfo(data)).catch(() => {});
  const saveContactInfo = async (e) => {
    e.preventDefault();
    setContactSaving(true);
    try {
      await api.put("/admin/contact-info", contactInfo);
      toast.success(lang === "ar" ? "تم حفظ بيانات التواصل" : "Contact info saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setContactSaving(false);
    }
  };

  const uploadCategoryImage = async (key, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(lang === "ar" ? "الصورة كبيرة (الحد 8 ميغا)" : "Image too large (max 8 MB)");
      return;
    }
    setCatUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const absoluteUrl = data.url.startsWith("http") ? data.url : `${backendUrl}${data.url}`;
      await api.put(`/admin/categories/${key}`, { image_url: absoluteUrl });
      toast.success(lang === "ar" ? "تم تحديث صورة الفئة" : "Category image updated");
      loadCategories();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCatUploading("");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(lang === "ar" ? "الصورة كبيرة (الحد 8 ميغا)" : "Image too large (max 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Prepend backend URL so <img src> always works, even from other apps
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const absoluteUrl = data.url.startsWith("http") ? data.url : `${backendUrl}${data.url}`;
      setForm((f) => ({ ...f, image_url: absoluteUrl }));
      toast.success(lang === "ar" ? "تم رفع الصورة" : "Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const loadProducts = () => api.get("/products").then(({ data }) => setProducts(data));
  const loadOrders = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: "", name: "", discount_percent: 10, days_valid: 30 });
  const loadCoupons = () => api.get("/admin/coupons").then(({ data }) => setCoupons(data)).catch(() => {});
  const [reports, setReports] = useState(null);
  const [stockNotifs, setStockNotifs] = useState([]);
  const loadReports = () => api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {});
  const loadStockNotifs = () => api.get("/admin/stock-notifications").then(({ data }) => setStockNotifs(data)).catch(() => {});

  const [loyalty, setLoyalty] = useState({ point_value_aed: 0.2, min_redeem: 5, tiers: [] });
  const [loyaltySaving, setLoyaltySaving] = useState(false);
  const loadLoyalty = () => api.get("/loyalty-config").then(({ data }) => setLoyalty(data)).catch(() => {});

  const [campaign, setCampaign] = useState({ enabled: false, name_en: "Double Points", name_ar: "ضعف النقاط", multiplier: 2, product_ids: [], starts_at: null, ends_at: null });
  const [campaignSaving, setCampaignSaving] = useState(false);
  const loadCampaign = () => api.get("/points-campaign").then(({ data }) => setCampaign(data)).catch(() => {});
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const l = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return l.toISOString().slice(0, 16);
  };
  const toggleCampaignProduct = (pid) => {
    setCampaign((prev) => {
      const ids = new Set(prev.product_ids || []);
      if (ids.has(pid)) ids.delete(pid); else ids.add(pid);
      return { ...prev, product_ids: [...ids] };
    });
  };
  const saveCampaign = async (e) => {
    e.preventDefault();
    setCampaignSaving(true);
    try {
      await api.put("/admin/points-campaign", {
        enabled: !!campaign.enabled,
        name_en: campaign.name_en,
        name_ar: campaign.name_ar,
        multiplier: parseFloat(campaign.multiplier) || 2,
        product_ids: campaign.product_ids || [],
        starts_at: campaign.starts_at || null,
        ends_at: campaign.ends_at || null,
      });
      toast.success(t("campaignSaved"));
      loadCampaign();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCampaignSaving(false);
    }
  };
  const updateTier = (idx, field, value) => {
    setLoyalty((prev) => {
      const tiers = [...(prev.tiers || [])];
      tiers[idx] = { ...tiers[idx], [field]: value };
      return { ...prev, tiers };
    });
  };
  const saveLoyalty = async (e) => {
    e.preventDefault();
    setLoyaltySaving(true);
    try {
      await api.put("/admin/loyalty-config", {
        point_value_aed: parseFloat(loyalty.point_value_aed) || 0,
        min_redeem: parseInt(loyalty.min_redeem) || 1,
        tiers: (loyalty.tiers || []).map((tr) => ({
          key: tr.key,
          name_en: tr.name_en,
          name_ar: tr.name_ar,
          min_points: parseInt(tr.min_points) || 0,
          multiplier: parseFloat(tr.multiplier) || 1,
          discount_percent: parseFloat(tr.discount_percent) || 0,
        })),
      });
      toast.success(t("loyaltySaved"));
      loadLoyalty();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoyaltySaving(false);
    }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", {
        code: newCoupon.code.trim().toUpperCase(),
        name: newCoupon.name.trim(),
        discount_percent: parseFloat(newCoupon.discount_percent),
        days_valid: parseInt(newCoupon.days_valid) || 30,
      });
      toast.success("Created");
      setNewCoupon({ code: "", name: "", discount_percent: 10, days_valid: 30 });
      loadCoupons();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeCoupon = async (code) => {
    try {
      await api.delete(`/admin/coupons/${code}`);
      toast.success("Deleted");
      loadCoupons();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyToClipboard = async (text, msg) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg || (lang === "ar" ? "تم النسخ" : "Copied"));
    } catch {
      // Fallback for older browsers or non-HTTPS contexts
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success(msg || (lang === "ar" ? "تم النسخ" : "Copied")); }
      catch { toast.error(lang === "ar" ? "تعذّر النسخ" : "Copy failed"); }
      document.body.removeChild(ta);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadProducts();
      loadOrders();
      loadCoupons();
      loadReports();
      loadStockNotifs();
      loadCategories();
      loadLoyalty();
      loadCampaign();
    }
  }, [isAdmin]);

  if (user === undefined) return <div className="p-20 text-center text-[#9CA3AF]">…</div>;
  if (!user || !isAdmin) return <Navigate to="/login" replace />;

  const startEdit = (p) => { setEditing(p.id); setForm(p); };
  const startNew = () => { setEditing("new"); setForm(empty); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) || 0, points: parseInt(form.points) || 0 };
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("Created");
      } else {
        await api.put(`/products/${editing}`, payload);
        toast.success("Updated");
      }
      cancel();
      loadProducts();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const askRemove = (p) => setConfirmDelete(p);

  const doRemove = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/products/${confirmDelete.id}`);
      toast.success(t("deleted"));
      setConfirmDelete(null);
      await loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const updateOrderStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success("Updated");
      loadOrders();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const advanceOrder = async (id) => {
    try {
      await api.put(`/admin/orders/${id}/advance`);
      toast.success(lang === "ar" ? "تم نقل الطلب للخطوة التالية" : "Moved to next step");
      loadOrders();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 page-enter">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl">{t("adminDashboard")}</h1>
        {tab === "products" && (
          <button onClick={startNew} data-testid="admin-add-product-btn" className="btn-gold px-5 py-2.5 rounded-full inline-flex items-center gap-2">
            <Plus size={16} /> {t("addProduct")}
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-8 border-b border-[#27272A] overflow-x-auto scrollbar-thin no-scrollbar">
        <button
          onClick={() => setTab("products")}
          data-testid="admin-tab-products"
          className={`px-5 py-3 inline-flex items-center gap-2 text-sm transition-all border-b-2 flex-shrink-0 whitespace-nowrap ${
            tab === "products" ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#9CA3AF] border-transparent"
          }`}
        >
          <Package size={16} /> {t("products")} <span className="num-ltr">({products.length})</span>
        </button>
        <button
          onClick={() => setTab("orders")}
          data-testid="admin-tab-orders"
          className={`px-5 py-3 inline-flex items-center gap-2 text-sm transition-all border-b-2 flex-shrink-0 whitespace-nowrap ${
            tab === "orders" ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#9CA3AF] border-transparent"
          }`}
        >
          <ShoppingCart size={16} /> {t("orders")} <span className="num-ltr">({orders.length})</span>
        </button>
        <button
          onClick={() => setTab("coupons")}
          data-testid="admin-tab-coupons"
          className={`px-5 py-3 inline-flex items-center gap-2 text-sm transition-all border-b-2 flex-shrink-0 whitespace-nowrap ${
            tab === "coupons" ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#9CA3AF] border-transparent"
          }`}
        >
          <Tag size={16} /> {lang === "ar" ? "أكواد الخصم" : "Coupons"} <span className="num-ltr">({coupons.length})</span>
        </button>
        <button
          onClick={() => setTab("reports")}
          data-testid="admin-tab-reports"
          className={`px-5 py-3 inline-flex items-center gap-2 text-sm transition-all border-b-2 flex-shrink-0 whitespace-nowrap ${
            tab === "reports" ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#9CA3AF] border-transparent"
          }`}
        >
          <BarChart3 size={16} /> {lang === "ar" ? "التقارير" : "Reports"}
        </button>
        <button
          onClick={() => setTab("categories")}
          data-testid="admin-tab-categories"
          className={`px-5 py-3 inline-flex items-center gap-2 text-sm transition-all border-b-2 flex-shrink-0 whitespace-nowrap ${
            tab === "categories" ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#9CA3AF] border-transparent"
          }`}
        >
          <ImageIcon size={16} /> {lang === "ar" ? "صور الفئات" : "Category images"} <span className="num-ltr">({categories.length})</span>
        </button>
        <button
          onClick={() => setTab("contact")}
          data-testid="admin-tab-contact"
          className={`px-5 py-3 inline-flex items-center gap-2 text-sm transition-all border-b-2 flex-shrink-0 whitespace-nowrap ${
            tab === "contact" ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#9CA3AF] border-transparent"
          }`}
        >
          <PhoneIcon size={16} /> {lang === "ar" ? "اتصل بنا" : "Contact info"}
        </button>
        <button
          onClick={() => setTab("loyalty")}
          data-testid="admin-tab-loyalty"
          className={`px-5 py-3 inline-flex items-center gap-2 text-sm transition-all border-b-2 flex-shrink-0 whitespace-nowrap ${
            tab === "loyalty" ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#9CA3AF] border-transparent"
          }`}
        >
          <Gift size={16} /> {t("loyaltySettings")}
        </button>
        <button
          onClick={() => setTab("stock")}
          data-testid="admin-tab-stock"
          className={`px-5 py-3 inline-flex items-center gap-2 text-sm transition-all border-b-2 flex-shrink-0 whitespace-nowrap ${
            tab === "stock" ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#9CA3AF] border-transparent"
          }`}
        >
          <Bell size={16} /> {lang === "ar" ? "تنبيهات المخزون" : "Stock alerts"} <span className="num-ltr">({stockNotifs.length})</span>
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur" data-testid="admin-edit-modal">
          <form onSubmit={save} className="bg-[#0A0A0B] border border-[#27272A] rounded-2xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl">{editing === "new" ? t("addProduct") : t("editProduct")}</h2>
              <button type="button" onClick={cancel} className="p-2 hover:text-[#D4AF37]" data-testid="admin-modal-close"><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required placeholder={t("nameEn")} value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className="input-luxe" data-testid="admin-name-en" />
              <input required placeholder={t("nameAr")} value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} className="input-luxe" data-testid="admin-name-ar" />
            </div>
            <textarea placeholder={t("descEn")} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="input-luxe" rows={2} data-testid="admin-desc-en" />
            <textarea placeholder={t("descAr")} value={form.description_ar} onChange={(e) => setForm({ ...form, description_ar: e.target.value })} className="input-luxe" rows={2} data-testid="admin-desc-ar" />
            <div className="grid sm:grid-cols-3 gap-3">
              <input required type="number" step="0.01" placeholder={t("price")} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-luxe" dir="ltr" data-testid="admin-price" />
              <input required type="number" placeholder={t("stock")} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="input-luxe" dir="ltr" data-testid="admin-stock" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-luxe" data-testid="admin-category">
                {CATS.map((c) => (<option key={c} value={c}>{t(c)}</option>))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#9CA3AF] bg-[#121214] border border-[#27272A] rounded-xl px-3 py-2">
              <Award size={16} className="text-[#D4AF37] flex-shrink-0" />
              <span className="flex-shrink-0">{t("pointsPerUnit")}:</span>
              <input
                type="number" min="0" step="1"
                value={form.points}
                onChange={(e) => setForm({ ...form, points: e.target.value })}
                className="input-luxe flex-1 py-1.5" dir="ltr" placeholder="0"
                data-testid="admin-points"
              />
            </label>
            <input required placeholder={t("imageUrl")} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="input-luxe" dir="ltr" data-testid="admin-image" />
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <label
                  className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition text-sm ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                  data-testid="admin-image-upload-label"
                >
                  <Upload size={14} />
                  {uploading
                    ? (lang === "ar" ? "جاري الرفع..." : "Uploading...")
                    : (lang === "ar" ? "رفع صورة من جهازك" : "Upload image")}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                    data-testid="admin-image-upload-input"
                  />
                </label>
                {form.image_url && (
                  <span className="text-[10px] text-[#9CA3AF] truncate max-w-[220px]" dir="ltr">
                    {form.image_url.slice(-50)}
                  </span>
                )}
              </div>
              {form.image_url && (
                <img
                  src={form.image_url}
                  alt=""
                  className="w-24 h-24 object-cover rounded-lg border border-[#27272A]"
                  data-testid="admin-image-preview"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#D4AF37]" data-testid="admin-featured" />
              {t("featuredFlag")}
            </label>
            <div className="flex gap-3 pt-3">
              <button type="submit" className="btn-gold flex-1 py-2.5 rounded-full" data-testid="admin-save-btn">{t("save")}</button>
              <button type="button" onClick={cancel} className="btn-outline-gold flex-1 py-2.5 rounded-full">{t("cancel")}</button>
            </div>
          </form>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur" data-testid="admin-confirm-delete-modal">
          <div className="bg-[#0A0A0B] border border-red-400/40 rounded-2xl w-full max-w-md p-6">
            <h3 className="font-display text-2xl mb-2 text-red-400">{t("confirmDeleteTitle")}</h3>
            <p className="text-sm text-[#9CA3AF] mb-3">{t("confirmDeleteMsg")}</p>
            <div className="text-sm text-[#F3F4F6] mb-6 p-3 bg-[#121214] border border-[#27272A] rounded-lg">
              {lang === "ar" ? confirmDelete.name_ar : confirmDelete.name_en}
            </div>
            <div className="flex gap-3">
              <button
                onClick={doRemove}
                disabled={deleting}
                data-testid="admin-confirm-delete-btn"
                className="flex-1 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white font-medium disabled:opacity-50 transition-colors"
              >
                {deleting ? "..." : t("yesDelete")}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                data-testid="admin-cancel-delete-btn"
                className="flex-1 btn-outline-gold py-2.5 rounded-full"
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "categories" && (
        <div className="space-y-4" data-testid="admin-categories">
          <div className="text-sm text-[#9CA3AF] mb-4">
            {lang === "ar"
              ? "اضغط على أي فئة لرفع صورة جديدة تظهر في الصفحة الرئيسية."
              : "Click any category to upload a new image that appears on the home page."}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((c) => {
              const busy = catUploading === c.key;
              return (
                <div
                  key={c.key}
                  className="bg-[#121214] border border-[#27272A] rounded-2xl p-4 space-y-3"
                  data-testid={`admin-category-${c.key}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg">{t(c.key)}</div>
                    <span className="text-[10px] uppercase tracking-widest text-[#9CA3AF]" dir="ltr">{c.key}</span>
                  </div>
                  <div className="aspect-[4/3] w-full rounded-lg overflow-hidden bg-[#0A0A0B] border border-[#27272A]">
                    {c.image_url ? (
                      <img
                        src={c.image_url}
                        alt={t(c.key)}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-[#9CA3AF] text-xs">
                        {lang === "ar" ? "لا توجد صورة" : "No image"}
                      </div>
                    )}
                  </div>
                  <label
                    className={`cursor-pointer w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition text-sm ${busy ? "opacity-50 pointer-events-none" : ""}`}
                    data-testid={`admin-category-upload-${c.key}`}
                  >
                    <Upload size={14} />
                    {busy
                      ? (lang === "ar" ? "جاري الرفع..." : "Uploading...")
                      : (lang === "ar" ? "رفع صورة جديدة" : "Upload new image")}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (f) uploadCategoryImage(c.key, f);
                      }}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "coupons" && (
        <div className="space-y-6" data-testid="admin-coupons">
          <form onSubmit={createCoupon} className="bg-[#121214] border border-[#27272A] rounded-2xl p-5 space-y-3">
            <div className="font-display text-lg mb-2">
              {lang === "ar" ? "إضافة كود خصم جديد" : "Add new coupon"}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <input
                required
                placeholder={lang === "ar" ? "الكود (مثل SUMMER20)" : "Code (e.g. SUMMER20)"}
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })}
                className="input-luxe"
                dir="ltr"
                data-testid="admin-coupon-code"
              />
              <input
                placeholder={lang === "ar" ? "الاسم (مثل عرض الصيف)" : "Name (e.g. Summer sale)"}
                value={newCoupon.name}
                onChange={(e) => setNewCoupon({ ...newCoupon, name: e.target.value })}
                className="input-luxe"
                data-testid="admin-coupon-name"
              />
              <input
                required
                type="number"
                min="1"
                max="100"
                step="1"
                placeholder={lang === "ar" ? "نسبة الخصم من 100" : "Discount % (0-100)"}
                value={newCoupon.discount_percent}
                onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: e.target.value })}
                className="input-luxe"
                dir="ltr"
                data-testid="admin-coupon-percent"
              />
              <input
                required
                type="number"
                min="1"
                step="1"
                placeholder={lang === "ar" ? "المدة بالأيام" : "Duration (days)"}
                value={newCoupon.days_valid}
                onChange={(e) => setNewCoupon({ ...newCoupon, days_valid: e.target.value })}
                className="input-luxe"
                dir="ltr"
                data-testid="admin-coupon-days"
              />
            </div>
            <button
              type="submit"
              className="btn-gold px-6 py-2.5 rounded-full inline-flex items-center gap-2"
              data-testid="admin-coupon-submit"
            >
              <Plus size={16} /> {lang === "ar" ? "إضافة الكود" : "Add coupon"}
            </button>
          </form>

          <div className="space-y-2" data-testid="admin-coupons-list">
            {coupons.length === 0 && (
              <div className="text-center py-12 text-[#9CA3AF]">
                {lang === "ar" ? "لا توجد أكواد بعد" : "No coupons yet"}
              </div>
            )}
            {coupons.map((c) => {
              const expiresAt = c.expires_at ? new Date(c.expires_at) : null;
              const daysLeft = expiresAt
                ? Math.max(0, Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24)))
                : "-";
              return (
                <div
                  key={c.code}
                  className="bg-[#121214] border border-[#27272A] rounded-xl p-4 flex flex-wrap justify-between items-center gap-4"
                  data-testid={`admin-coupon-row-${c.code}`}
                >
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-display text-lg num-ltr text-[#D4AF37]">{c.code}</div>
                    <div className="text-xs text-[#9CA3AF]">
                      {c.name || (lang === "ar" ? "بدون اسم" : "No label")}
                    </div>
                  </div>
                  <div className="text-center min-w-[80px]">
                    <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">
                      {lang === "ar" ? "الخصم" : "Discount"}
                    </div>
                    <div className="font-display text-lg num-ltr">{c.discount_percent}%</div>
                  </div>
                  <div className="text-center min-w-[100px]">
                    <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">
                      {lang === "ar" ? "المتبقي" : "Days left"}
                    </div>
                    <div className="font-display text-lg num-ltr">{daysLeft}</div>
                  </div>
                  <button
                    onClick={() => removeCoupon(c.code)}
                    data-testid={`admin-coupon-delete-${c.code}`}
                    className="p-2 border border-[#27272A] rounded hover:border-red-400 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "reports" && reports && (
        <div className="space-y-6" data-testid="admin-reports">
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { label: lang === "ar" ? "اليوم" : "Today", val: reports.revenue_today },
              { label: lang === "ar" ? "هذا الشهر" : "This month", val: reports.revenue_month },
              { label: lang === "ar" ? "الإجمالي" : "All time", val: reports.revenue_total },
            ].map((c, i) => (
              <div key={i} className="bg-[#121214] border border-[#27272A] rounded-2xl p-6">
                <div className="text-xs uppercase tracking-widest text-[#9CA3AF] mb-2">{c.label}</div>
                <div className="font-display text-3xl text-[#D4AF37] num-ltr">{c.val.toFixed(2)}</div>
                <div className="text-xs text-[#9CA3AF]">{reports.currency}</div>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-[#121214] border border-[#27272A] rounded-xl p-5">
              <div className="text-xs text-[#9CA3AF] mb-1">{lang === "ar" ? "إجمالي الطلبات" : "Total orders"}</div>
              <div className="font-display text-2xl num-ltr">{reports.orders_count}</div>
            </div>
            <div className="bg-[#121214] border border-[#27272A] rounded-xl p-5">
              <div className="text-xs text-[#9CA3AF] mb-1">{lang === "ar" ? "طلبات مدفوعة" : "Paid orders"}</div>
              <div className="font-display text-2xl num-ltr text-[#10B981]">{reports.paid_orders_count}</div>
            </div>
          </div>
          <div>
            <div className="font-display text-lg mb-3">{lang === "ar" ? "الأكثر مبيعاً" : "Top sellers"}</div>
            <div className="space-y-2">
              {reports.top_products.map((p, i) => (
                <div key={i} className="flex justify-between items-center bg-[#121214] border border-[#27272A] rounded-lg px-4 py-3">
                  <div className="text-sm">{lang === "ar" ? p.name_ar : p.name_en}</div>
                  <div className="text-xs text-[#D4AF37] num-ltr">{p.qty}× · {p.revenue.toFixed(2)} {reports.currency}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "contact" && (
        <div className="max-w-2xl" data-testid="admin-contact">
          <form onSubmit={saveContactInfo} className="bg-[#121214] border border-[#27272A] rounded-2xl p-6 space-y-4">
            <div className="font-display text-lg mb-2">
              {lang === "ar" ? "تعديل بيانات صفحة اتصل بنا" : "Edit Contact page details"}
            </div>
            <p className="text-xs text-[#9CA3AF] mb-4">
              {lang === "ar"
                ? "تظهر هذه المعلومات للزبائن في صفحة /contact. اترك الحقل فارغاً لإخفائه."
                : "These appear to customers on /contact. Leave a field empty to hide it."}
            </p>

            <label className="block text-xs text-[#9CA3AF]">
              {lang === "ar" ? "📞 رقم الهاتف" : "📞 Phone"}
              <input
                type="tel"
                dir="ltr"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                placeholder="+971 50 000 0000"
                className="input-luxe mt-1"
                data-testid="admin-contact-phone"
              />
            </label>

            <label className="block text-xs text-[#9CA3AF]">
              {lang === "ar" ? "💬 رقم WhatsApp" : "💬 WhatsApp"}
              <input
                type="tel"
                dir="ltr"
                value={contactInfo.whatsapp}
                onChange={(e) => setContactInfo({ ...contactInfo, whatsapp: e.target.value })}
                placeholder="+971 50 000 0000"
                className="input-luxe mt-1"
                data-testid="admin-contact-whatsapp"
              />
            </label>

            <label className="block text-xs text-[#9CA3AF]">
              {lang === "ar" ? "📧 البريد الإلكتروني" : "📧 Email"}
              <input
                type="email"
                dir="ltr"
                value={contactInfo.email}
                onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                placeholder="support@m16.ae"
                className="input-luxe mt-1"
                data-testid="admin-contact-email"
              />
            </label>

            <label className="block text-xs text-[#9CA3AF]">
              {lang === "ar" ? "📍 العنوان (عربي)" : "📍 Address (Arabic)"}
              <input
                type="text"
                value={contactInfo.address_ar}
                onChange={(e) => setContactInfo({ ...contactInfo, address_ar: e.target.value })}
                placeholder="دبي، الإمارات"
                className="input-luxe mt-1"
                data-testid="admin-contact-address-ar"
              />
            </label>

            <label className="block text-xs text-[#9CA3AF]">
              {lang === "ar" ? "📍 العنوان (إنجليزي)" : "📍 Address (English)"}
              <input
                type="text"
                dir="ltr"
                value={contactInfo.address_en}
                onChange={(e) => setContactInfo({ ...contactInfo, address_en: e.target.value })}
                placeholder="Dubai, UAE"
                className="input-luxe mt-1"
                data-testid="admin-contact-address-en"
              />
            </label>

            <div className="pt-3">
              <button
                type="submit"
                disabled={contactSaving}
                data-testid="admin-contact-save-btn"
                className="btn-gold px-8 py-2.5 rounded-full inline-flex items-center gap-2"
              >
                {contactSaving
                  ? (lang === "ar" ? "جاري الحفظ..." : "Saving...")
                  : (lang === "ar" ? "حفظ التغييرات" : "Save changes")}
              </button>
            </div>
          </form>
        </div>
      )}

      {tab === "loyalty" && (
        <div className="max-w-3xl" data-testid="admin-loyalty">
          <form onSubmit={saveLoyalty} className="bg-[#121214] border border-[#27272A] rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-2 font-display text-lg mb-2">
              <Gift size={18} className="text-[#D4AF37]" />
              {t("loyaltySettings")}
            </div>
            <p className="text-xs text-[#9CA3AF] mb-4">
              {lang === "ar"
                ? "يحصل العملاء المسجّلون على نقاط حسب المنتجات، ويستبدلونها بخصم عند الدفع. حدّد قيمة النقطة والحد الأدنى للاستبدال."
                : "Registered customers earn points per product and redeem them for a discount at checkout. Set the point value and the minimum to redeem."}
            </p>

            <label className="block text-xs text-[#9CA3AF]">
              {t("pointValueLabel")}
              <input
                type="number" min="0" step="0.01" dir="ltr"
                value={loyalty.point_value_aed}
                onChange={(e) => setLoyalty({ ...loyalty, point_value_aed: e.target.value })}
                className="input-luxe mt-1"
                data-testid="admin-loyalty-value"
              />
            </label>

            <label className="block text-xs text-[#9CA3AF]">
              {t("minRedeemLabel")}
              <input
                type="number" min="1" step="1" dir="ltr"
                value={loyalty.min_redeem}
                onChange={(e) => setLoyalty({ ...loyalty, min_redeem: e.target.value })}
                className="input-luxe mt-1"
                data-testid="admin-loyalty-min"
              />
            </label>

            <div className="text-xs text-[#D4AF37] bg-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-lg p-3 num-ltr" dir="ltr">
              {parseInt(loyalty.min_redeem) || 0} pts = {((parseFloat(loyalty.point_value_aed) || 0) * (parseInt(loyalty.min_redeem) || 0)).toFixed(2)} {t("currency")}
            </div>

            {/* Tiers editor */}
            <div className="pt-4 border-t border-[#27272A]">
              <div className="flex items-center gap-2 font-display mb-3">
                <Award size={16} className="text-[#D4AF37]" /> {t("manageTiers")}
              </div>
              <div className="space-y-3">
                {(loyalty.tiers || []).map((tr, idx) => (
                  <div key={tr.key} className="bg-[#0A0A0B]/50 border border-[#27272A] rounded-xl p-3" data-testid={`admin-tier-${tr.key}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                      <label className="block text-[10px] text-[#9CA3AF]">
                        {t("tierNameLabel")} (ع)
                        <input value={tr.name_ar} onChange={(e) => updateTier(idx, "name_ar", e.target.value)} className="input-luxe mt-1 py-1.5 text-sm" data-testid={`tier-name-ar-${tr.key}`} />
                      </label>
                      <label className="block text-[10px] text-[#9CA3AF]">
                        {t("tierNameLabel")} (EN)
                        <input value={tr.name_en} onChange={(e) => updateTier(idx, "name_en", e.target.value)} className="input-luxe mt-1 py-1.5 text-sm" dir="ltr" data-testid={`tier-name-en-${tr.key}`} />
                      </label>
                      <label className="block text-[10px] text-[#9CA3AF]">
                        {t("tierMinLabel")}
                        <input type="number" min="0" value={tr.min_points} onChange={(e) => updateTier(idx, "min_points", e.target.value)} className="input-luxe mt-1 py-1.5 text-sm" dir="ltr" data-testid={`tier-min-${tr.key}`} />
                      </label>
                      <label className="block text-[10px] text-[#9CA3AF]">
                        {t("tierMultLabel")}
                        <input type="number" min="0" step="0.05" value={tr.multiplier} onChange={(e) => updateTier(idx, "multiplier", e.target.value)} className="input-luxe mt-1 py-1.5 text-sm" dir="ltr" data-testid={`tier-mult-${tr.key}`} />
                      </label>
                      <label className="block text-[10px] text-[#9CA3AF]">
                        {t("tierDiscLabel")}
                        <input type="number" min="0" step="1" value={tr.discount_percent} onChange={(e) => updateTier(idx, "discount_percent", e.target.value)} className="input-luxe mt-1 py-1.5 text-sm" dir="ltr" data-testid={`tier-disc-${tr.key}`} />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loyaltySaving}
                data-testid="admin-loyalty-save-btn"
                className="btn-gold px-8 py-2.5 rounded-full inline-flex items-center gap-2"
              >
                {loyaltySaving ? (lang === "ar" ? "جاري الحفظ..." : "Saving...") : (lang === "ar" ? "حفظ التغييرات" : "Save changes")}
              </button>
            </div>
          </form>

          {/* Double points campaign */}
          <form onSubmit={saveCampaign} className="bg-[#121214] border border-[#27272A] rounded-2xl p-6 space-y-4 mt-6" data-testid="admin-campaign">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 font-display text-lg">
                <Sparkles size={18} className="text-[#D4AF37]" />
                {t("manageCampaign")}
              </div>
              <span className={`text-[10px] px-2 py-1 rounded-full num-ltr ${campaign.active ? "bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30" : "bg-[#27272A] text-[#9CA3AF]"}`}>
                {campaign.active ? t("campaignLive") : t("campaignScheduled")}
              </span>
            </div>

            <label className="flex items-center gap-2 text-sm text-[#F3F4F6]/85 cursor-pointer">
              <input type="checkbox" checked={!!campaign.enabled} onChange={(e) => setCampaign({ ...campaign, enabled: e.target.checked })} className="accent-[#D4AF37]" data-testid="campaign-enabled" />
              {t("campaignEnabled")}
            </label>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-xs text-[#9CA3AF]">
                {t("campaignName")} (ع)
                <input value={campaign.name_ar} onChange={(e) => setCampaign({ ...campaign, name_ar: e.target.value })} className="input-luxe mt-1 py-1.5 text-sm" data-testid="campaign-name-ar" />
              </label>
              <label className="block text-xs text-[#9CA3AF]">
                {t("campaignName")} (EN)
                <input value={campaign.name_en} onChange={(e) => setCampaign({ ...campaign, name_en: e.target.value })} className="input-luxe mt-1 py-1.5 text-sm" dir="ltr" data-testid="campaign-name-en" />
              </label>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <label className="block text-xs text-[#9CA3AF]">
                {t("campaignMultiplier")}
                <input type="number" min="1" step="0.5" value={campaign.multiplier} onChange={(e) => setCampaign({ ...campaign, multiplier: e.target.value })} className="input-luxe mt-1 py-1.5 text-sm" dir="ltr" data-testid="campaign-multiplier" />
              </label>
              <label className="block text-xs text-[#9CA3AF]">
                {t("campaignStart")}
                <input type="datetime-local" value={toLocalInput(campaign.starts_at)} onChange={(e) => setCampaign({ ...campaign, starts_at: e.target.value ? new Date(e.target.value).toISOString() : null })} className="input-luxe mt-1 py-1.5 text-sm" dir="ltr" data-testid="campaign-start" />
              </label>
              <label className="block text-xs text-[#9CA3AF]">
                {t("campaignEnd")}
                <input type="datetime-local" value={toLocalInput(campaign.ends_at)} onChange={(e) => setCampaign({ ...campaign, ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })} className="input-luxe mt-1 py-1.5 text-sm" dir="ltr" data-testid="campaign-end" />
              </label>
            </div>

            <div>
              <div className="text-xs text-[#9CA3AF] mb-2">{t("campaignProducts")} ({(campaign.product_ids || []).length})</div>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-1">
                {products.map((p) => {
                  const on = (campaign.product_ids || []).includes(p.id);
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => toggleCampaignProduct(p.id)}
                      data-testid={`campaign-product-${p.id}`}
                      className={`text-[11px] px-3 py-1.5 rounded-full border transition-all ${on ? "bg-[#D4AF37] text-[#0A0A0B] border-[#D4AF37]" : "border-[#27272A] text-[#9CA3AF] hover:border-[#D4AF37]/50"}`}
                    >
                      {lang === "ar" ? p.name_ar : p.name_en}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pt-1">
              <button type="submit" disabled={campaignSaving} data-testid="admin-campaign-save-btn" className="btn-gold px-8 py-2.5 rounded-full">
                {campaignSaving ? (lang === "ar" ? "جاري الحفظ..." : "Saving...") : (lang === "ar" ? "حفظ العرض" : "Save campaign")}
              </button>
            </div>
          </form>
        </div>
      )}
        <div className="space-y-2" data-testid="admin-stock-list">
          {stockNotifs.length === 0 && <div className="text-center py-12 text-[#9CA3AF]">—</div>}
          {stockNotifs.map((n, i) => {
            const p = products.find((pp) => pp.id === n.product_id);
            return (
              <div key={i} className="bg-[#121214] border border-[#27272A] rounded-xl p-4 flex justify-between items-center">
                <div>
                  <div className="text-sm">{p ? (lang === "ar" ? p.name_ar : p.name_en) : n.product_id}</div>
                  <div className="text-xs text-[#9CA3AF] num-ltr">{n.email}</div>
                </div>
                <div className="text-xs text-[#9CA3AF] num-ltr">{new Date(n.created_at).toLocaleDateString()}</div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "products" && (
        <div className="space-y-3" data-testid="admin-products-list">
          {(() => {
            const outCount = products.filter((p) => (p.stock ?? 0) <= 0).length;
            const lowCount = products.filter((p) => (p.stock ?? 0) > 0 && (p.stock ?? 0) <= 5).length;
            if (outCount === 0 && lowCount === 0) return null;
            return (
              <div className="flex items-center gap-2 flex-wrap bg-amber-500/5 border border-amber-500/30 rounded-xl p-3 text-xs" data-testid="low-stock-banner">
                <Bell size={14} className="text-amber-400" />
                {lowCount > 0 && (
                  <span className="text-amber-300 num-ltr">{t("lowStock")}: {lowCount}</span>
                )}
                {outCount > 0 && (
                  <span className="text-red-300 num-ltr">{t("soldOut")}: {outCount}</span>
                )}
              </div>
            );
          })()}
          {products.length === 0 && <div className="text-center py-12 text-[#9CA3AF]">—</div>}
          {products.map((p) => {
            const out = (p.stock ?? 0) <= 0;
            const low = !out && (p.stock ?? 0) <= 5;
            return (
            <div key={p.id} className={`bg-[#121214] border rounded-xl p-4 flex gap-4 ${out ? "border-red-500/40" : low ? "border-amber-500/40" : "border-[#27272A]"}`}>
              <div className="relative w-20 h-20 flex-shrink-0">
                <img src={p.image_url} alt="" className={`w-20 h-20 object-cover rounded-lg ${out ? "opacity-40 grayscale" : ""}`} />
                {out && (
                  <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-red-300 text-center leading-tight px-1">
                    {t("soldOut")}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase text-[#9CA3AF]">{t(p.category)}</div>
                <div className="text-sm leading-snug mb-1">{lang === "ar" ? p.name_ar : p.name_en}</div>
                <div className="text-[#D4AF37] num-ltr text-sm">{p.price} {t("currency")}</div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  {out ? (
                    <span data-testid={`admin-soldout-${p.id}`} className="inline-flex items-center gap-1 text-[10px] text-red-300 num-ltr px-2 py-0.5 rounded-full border border-red-500/40 bg-red-500/10">
                      {t("soldOut")}
                    </span>
                  ) : low ? (
                    <span data-testid={`admin-lowstock-${p.id}`} className="inline-flex items-center gap-1 text-[10px] text-amber-300 num-ltr px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10">
                      <Bell size={10} /> {t("onlyLeft").replace("{n}", p.stock)}
                    </span>
                  ) : (
                    <span data-testid={`admin-stock-qty-${p.id}`} className="inline-flex items-center gap-1 text-[10px] text-[#10B981] num-ltr px-2 py-0.5 rounded-full border border-[#10B981]/30">
                      <Package size={10} /> {t("inStockQty")}: {p.stock}
                    </span>
                  )}
                  {p.points > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#D4AF37]/90 num-ltr px-2 py-0.5 rounded-full border border-[#D4AF37]/30">
                      <Award size={10} /> +{p.points} {t("points")}
                    </span>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => startEdit(p)} data-testid={`admin-edit-${p.id}`} className="p-1.5 border border-[#27272A] rounded hover:border-[#D4AF37]"><Edit2 size={14} /></button>
                  <button onClick={() => askRemove(p)} data-testid={`admin-delete-${p.id}`} className="p-1.5 border border-[#27272A] rounded hover:border-red-400 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3" data-testid="admin-orders-list">
          {orders.length === 0 && <div className="text-center py-12 text-[#9CA3AF]">—</div>}
          {orders.map((o) => (
            <div key={o.id} className="bg-[#121214] border border-[#27272A] rounded-xl p-5">
              <div className="flex justify-between items-start mb-3 flex-wrap gap-3">
                <div className="min-w-[240px]">
                  <div className="text-xs text-[#9CA3AF]">#<span className="num-ltr">{o.id.slice(0,8)}</span> · {new Date(o.created_at).toLocaleString()}</div>
                  <div className="font-display">{o.shipping?.name}</div>

                  {/* Contact — phone + email with copy / dial / mail buttons */}
                  <div className="mt-1 space-y-1 text-xs">
                    {o.shipping?.phone && (
                      <div className="flex items-center gap-2 flex-wrap" dir="ltr">
                        <a
                          href={`tel:${o.shipping.phone}`}
                          data-testid={`admin-order-phone-${o.id}`}
                          className="num-ltr text-[#F3F4F6]/85 hover:text-[#D4AF37] transition"
                        >
                          {o.shipping.phone}
                        </a>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(
                            o.shipping.phone,
                            lang === "ar" ? "تم نسخ رقم الهاتف" : "Phone copied"
                          )}
                          data-testid={`admin-copy-phone-${o.id}`}
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-[#27272A] text-[#9CA3AF] hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
                        >
                          <Copy size={10} /> {lang === "ar" ? "نسخ" : "Copy"}
                        </button>
                      </div>
                    )}
                    {(() => {
                      const em = o.shipping?.email || o.user_email;
                      // Only show if it looks like an actual email (guards against legacy orders where user_email was set to the shipping name).
                      if (!em || !em.includes("@")) return null;
                      return (
                        <div className="flex items-center gap-2 flex-wrap" dir="ltr">
                          <a
                            href={`mailto:${em}`}
                            data-testid={`admin-order-email-${o.id}`}
                            className="text-[#F3F4F6]/85 hover:text-[#D4AF37] transition truncate max-w-[220px]"
                          >
                            {em}
                          </a>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(em, lang === "ar" ? "تم نسخ الإيميل" : "Email copied")}
                            data-testid={`admin-copy-email-${o.id}`}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-[#27272A] text-[#9CA3AF] hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
                          >
                            <Copy size={10} /> {lang === "ar" ? "نسخ" : "Copy"}
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="text-xs text-[#9CA3AF] mt-1">{o.shipping?.address}, {o.shipping?.city}</div>
                </div>
                <div className="text-right">
                  <div className="text-[#D4AF37] font-display num-ltr">{o.total.toFixed(2)} {t("currency")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">{o.payment_method} · {o.payment_status}</div>
                  <select
                    value={o.order_status}
                    onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                    data-testid={`admin-order-status-${o.id}`}
                    className="mt-2 input-luxe text-xs py-1"
                  >
                    {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                  {o.order_status !== "delivered" && o.order_status !== "cancelled" && (
                    <button
                      onClick={() => advanceOrder(o.id)}
                      data-testid={`admin-advance-${o.id}`}
                      className="mt-2 w-full btn-gold text-xs py-1.5 rounded-full inline-flex items-center justify-center gap-1"
                    >
                      {t("nextStep")} <span className="rtl:hidden">→</span><span className="hidden rtl:inline">←</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="mb-3 py-3 px-1 bg-[#0A0A0B]/40 rounded-lg border border-[#27272A]" data-testid={`admin-order-timeline-${o.id}`}>
                <OrderTimeline status={o.order_status} />
              </div>
              <div className="text-xs text-[#9CA3AF] flex flex-wrap gap-2 mt-2">
                {o.items.map((i) => (
                  <span key={i.product_id} className="px-2 py-1 border border-[#27272A] rounded">
                    {lang === "ar" ? i.name_ar : i.name_en} × <span className="num-ltr">{i.quantity}</span>
                  </span>
                ))}
              </div>

              {((o.points_earned || 0) > 0 || (o.points_redeemed || 0) > 0) && (
                <div className="flex flex-wrap gap-2 mt-2 text-[10px]">
                  {(o.points_earned || 0) > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] num-ltr">
                      <Award size={10} /> +{o.points_earned} {t("points")}
                    </span>
                  )}
                  {(o.points_redeemed || 0) > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#10B981]/30 text-[#10B981] num-ltr">
                      <Gift size={10} /> −{o.points_redeemed} {t("points")} ({(o.points_discount || 0).toFixed(2)} {t("currency")})
                    </span>
                  )}
                </div>
              )}

              {o.shipping?.location?.lat && o.shipping?.location?.lng && (
                <div
                  className="mt-4 pt-4 border-t border-[#27272A] space-y-2"
                  data-testid={`admin-order-location-${o.id}`}
                >
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#D4AF37]">
                    <MapPin size={14} />
                    {lang === "ar" ? "موقع التوصيل على الخريطة" : "Delivery pin on map"}
                  </div>

                  {o.shipping.location.address && (
                    <div className="text-xs text-[#F3F4F6]/80 leading-relaxed">
                      {o.shipping.location.address}
                    </div>
                  )}

                  <div className="text-xs num-ltr text-[#9CA3AF]" dir="ltr">
                    {Number(o.shipping.location.lat).toFixed(6)}, {Number(o.shipping.location.lng).toFixed(6)}
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => copyToClipboard(
                        `${o.shipping.location.lat},${o.shipping.location.lng}`,
                        lang === "ar" ? "تم نسخ الإحداثيات" : "Coordinates copied"
                      )}
                      data-testid={`admin-copy-coords-${o.id}`}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition"
                    >
                      <Copy size={12} /> {lang === "ar" ? "نسخ الإحداثيات" : "Copy coords"}
                    </button>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(
                        `https://www.google.com/maps?q=${o.shipping.location.lat},${o.shipping.location.lng}`,
                        lang === "ar" ? "تم نسخ رابط الخريطة" : "Map link copied"
                      )}
                      data-testid={`admin-copy-maps-${o.id}`}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#27272A] text-[#F3F4F6]/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
                    >
                      <Copy size={12} /> {lang === "ar" ? "نسخ رابط Google Maps" : "Copy Maps link"}
                    </button>
                    {o.shipping.location.address && (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(
                          o.shipping.location.address,
                          lang === "ar" ? "تم نسخ العنوان" : "Address copied"
                        )}
                        data-testid={`admin-copy-address-${o.id}`}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#27272A] text-[#F3F4F6]/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
                      >
                        <Copy size={12} /> {lang === "ar" ? "نسخ العنوان" : "Copy address"}
                      </button>
                    )}
                    <a
                      href={`https://www.google.com/maps?q=${o.shipping.location.lat},${o.shipping.location.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`admin-open-maps-${o.id}`}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#27272A] text-[#F3F4F6]/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
                    >
                      <ExternalLink size={12} /> {lang === "ar" ? "فتح على الخرائط" : "Open in Maps"}
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
